import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Loader2, Building2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ManageSectors = () => {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    icon: 'Building2',
    description: '',
    expertise: ''
  });

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/sectors/`, { withCredentials: true });
      setSectors(response.data);
    } catch (error) {
      console.error('Error fetching sectors:', error);
      alert('Errore nel caricamento dei settori');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      expertise: formData.expertise.split(',').map(s => s.trim()).filter(Boolean)
    };

    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/sectors/${editingId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/sectors/`, payload, { withCredentials: true });
      }
      await fetchSectors();
      resetForm();
      alert(editingId ? 'Settore aggiornato!' : 'Settore aggiunto!');
    } catch (error) {
      console.error('Error saving sector:', error);
      alert('Errore nel salvaggio. Riprova.');
    }
  };

  const handleEdit = (sector) => {
    setFormData({
      name: sector.name || '',
      icon: sector.icon || 'Building2',
      description: sector.description || '',
      expertise: (sector.expertise || []).join(', ')
    });
    setEditingId(sector.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo settore?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/sectors/${id}`, { withCredentials: true });
      await fetchSectors();
      alert('Settore eliminato!');
    } catch (error) {
      console.error('Error deleting sector:', error);
      alert('Errore nell\'eliminazione');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      icon: 'Building2',
      description: '',
      expertise: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-900" />
      </div>
    );
  }

  const iconOptions = ['Building2', 'Cpu', 'TrendingUp', 'Home', 'HeartPulse', 'Zap', 'Factory', 'ShoppingBag', 'Layers'];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-serif text-gray-900">Gestione Sectors</h2>
          <p className="text-gray-600 mt-1">{sectors.length} settori totali</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
        >
          {showForm ? <X className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
          <span>{showForm ? 'Chiudi' : 'Nuovo Settore'}</span>
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingId ? 'Modifica Settore' : 'Nuovo Settore'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Es: Financial Services"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
              <select
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              >
                {iconOptions.map(icon => <option key={icon} value={icon}>{icon}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione *</label>
              <textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Expertise (separati da virgola)</label>
              <textarea
                value={formData.expertise}
                onChange={(e) => setFormData({ ...formData, expertise: e.target.value })}
                rows={3}
                placeholder="Es: Banking & Finance Regulation, Investment Funds, Fintech"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-3 mt-6">
            <button
              type="submit"
              className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Save className="h-5 w-5" />
              <span>{editingId ? 'Aggiorna' : 'Salva'}</span>
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Annulla
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Icon</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrizione</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sectors.map((sector) => (
              <tr key={sector.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sector.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700 font-mono">
                    {sector.icon}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  <div className="line-clamp-2">{sector.description}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleEdit(sector)}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(sector.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sectors.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Nessun settore presente</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageSectors;